<template>
    <div class="sfc-student-loan-calc">
        <div class="container-fluid">
            <div class="sfc-container">
				<h1>Student Loan Payoff Calculator</h1>
				<!--Schema Markup FAQs-->
				<script v-html="create_schema(FAQList)" type="application/ld+json"/>
				<p class="gray-text">
					Thinking about becoming debt-free faster? If you're looking to make extra payments now, use this student loan payoff calculator to estimate how much time and money you will save.
				</p>
				<div class="sfc-card">
                    <!-- This is the results Block BEGIN
                    Apply the class "hide" to this block before a user has entered their information.
                    -->
					<h4>
						<div v-if="resultDisplayed" class="social-media-area-floated mt-4">
							<sfc-share-component :hero="`https://web-resources.savingforcollege.com/images/cost-of-deferment-calculator-thumbnail.jpg`" title="Student Loan Payoff Calculator" :url="`${this.$store.state.config.sfcHost}/calculators/student-loan-payoff-calculator`" ></sfc-share-component>
						</div>
					</h4>
                    <div id="res" v-if="resultDisplayed" class="sfc-row border-bottom block text-center content">
                        <h4>Great news! Your results are below</h4>
                        <div class="sfc-col-12 flex">
                            <div class="sfc-col-6 small-card">
                                <img :src="`${$store.state.config.s3ResourceHost}/images/student-loans/time.svg`" alt="info icon vector"/>
                                <h2 class="dark-text">{{output.timeSaved}} Months</h2>
                                <p class="gray-text">How many fewer months you will have student loan payments</p>
                            </div>
                            <div class="sfc-col-6 small-card">
                                <img :src="`${$store.state.config.s3ResourceHost}/images/student-loans/money.svg`" alt="info icon vector"/>
                                <h2 class="dark-text">{{formatNumber(output.moneySaved)}}</h2>
                                <p class="gray-text">How much less you will pay in total</p>
                            </div>
                        </div>
												<div class="sfc-col-12">
													<script defer src='https://sfc-external-widgets.savingforcollege.com/student_loan_widget_v2/production/loaders/loader.js?loan_type=originator&audience=refinance'></script>
												</div>
						<div class="content text-left pt-0">
							<div class="sfc-col-12">
								<h4 class="cardTitle ml-0 pl-0 mb-5">Refinancing at a lower interest rate will also save you money:</h4>

								<script defer src='https://sfc-external-widgets.savingforcollege.com/refinance_widget_v3/production/loaders/loader.js'></script>
							</div>
						</div>
                        <!-- <div class="sfc-col-12 block">
                            <p class="dark-text">Did you know refinancing reduces total payments by thousands of dollars on average?</p>
                            <p class="gray-text">Check out our student loan refinancing rankings to find the best lender for you.
                            </p>
                            <button class="btn btn-secondary">
								View Refi Rankings
                             </button>
                        </div> -->
                    </div>
                    <!-- This is the results Block END-->
                    <!-- Form Block BEGIN-->
					<form @submit.prevent="recalculate" class="form sfc-col-12">
						<h4 class="cardTitle">
							Please enter your loan information.
							<div v-if="!resultDisplayed" class="social-media-area-floated">
								<sfc-share-component :hero="`https://web-resources.savingforcollege.com/images/cost-of-deferment-calculator-thumbnail.jpg`" title="Student Loan Payoff Calculator" :url="`${this.$store.state.config.sfcHost}/calculators/student-loan-payoff-calculator`" ></sfc-share-component>
							</div>
						</h4>
						<div class="sfc-row flex content align-top">
							<div class="sfc-col-6">
                                 <div class="input-group">
									<label for="current-loan" class="label"
										>Current Loan</label
									>
									<div class="inner">
										<input
											placeholder=""
											class="input postend"
											type="number"
                                            min="100"
                                            max="100000000000"
                                            step="any"
                                            v-model="input.current_loan"
											name="current-loan"
											id="current-loan"
                                            required
										/>
										<span class="end">$</span>
										<div class="tooltip">
											<img
												:src="`${$store.state.config.s3ResourceHost}/images/student-loans/info@2x.svg`"
												alt="info icon vector"
												class="info"
											/>
											<span class="tooltiptext"
												>The total amount you owe as of today. An estimate is just fine!</span
											>
										</div>
									</div>
                                </div>
                                <div class="input-group">
									<label for="interest-rate" class="label">
										Current Interest Rate</label
									>
									<div class="inner">
										<input
											placeholder=""
											class="input postend"
											type="number"
                                            min="0.1"
                                            max="100"
                                            step="any"
                                            v-model="input.interest_rate"
											name="interest-rate"
											id="interest-rate"
                                            required
										/>
										<span class="end">%</span>
										<div class="tooltip">
											<img
												:src="`${$store.state.config.s3ResourceHost}/images/student-loans/info@2x.svg`"
												alt="info icon vector"
												class="info"
											/>
											<span class="tooltiptext"
												>The <a href="/article/how-student-loan-interest-works" target="_blank">interest rate</a> you are currently paying, or the average interest rate across the multiple loans you have. Not sure what that is? Try a standard 5% for starters and play what-if games with different rates.</span
											>
										</div>
									</div>
                                </div>
                                <div class="input-group">
									<label for="monthly-payment" class="label">
										Current Monthly Payment</label
									>
									<div class="inner">
										<input
											placeholder=""
											class="input postend"
											type="number"
                                            :min="calculatedMinMonthlyPayment"
                                            max="1000000"
                                            step="any"
                                            v-model="input.monthly_payment"
											name="monthly-payment"
											id="monthly-payment"
											ref="monthlyPayment"
                                            required
										/>
										<span class="end">$</span>
										<div class="tooltip">
											<img
												:src="`${$store.state.config.s3ResourceHost}/images/student-loans/info@2x.svg`"
												alt="info icon vector"
												class="info"
											/>
											<span class="tooltiptext"
												>The amount you are currently paying per month for your loan. An estimate is just fine! If you have a 10-year term, 1% of the loan balance will be in the right ballpark.</span
											>
										</div>
									</div>
								</div>
							</div>
                            <div class="sfc-col-6">
                                <div class="input-group">
                                    <label for="add-to-payment" class="label"
										>Select Type</label
									>
                                    <div class="flex checkbox-group">

                                        <div class="input-group">
                                            <input
                                                type="radio"
                                                class="custom-checkbox"
                                                id="pay-type-payextra"
                                                name="pay-type"
                                                value="Pay Extra"
                                                v-model="input.pay_type"
                                            />
                                            <label for="pay-type-payextra">Pay Extra</label>
                                        </div>
                                        <div class="input-group">
                                            <input
                                                type="radio"
                                                class="custom-checkbox"
                                                id="pay-type-payof"
                                                name="pay-type"
                                                value="Pay Of By"
                                                v-model="input.pay_type"
                                            />
                                            <label for="pay-type-payof">Pay Off By</label>
                                        </div>
                                    </div>
                                </div>
                                <div class="input-group" v-if="input.pay_type=='Pay Extra'">
									<label for="add-to-payment" class="label"
										>Add to your payment by</label
									>
									<div class="inner">
										<input
											placeholder=""
											class="input postend"
											type="number"
                                            min="0.1"
                                            step="any"
											max="100000000"
										    v-model="input.add_to_payment"
											name="add-to-payment"
											id="add-to-payment"
                                            required
										/>
										<span class="end">$</span>
										<div class="tooltip">
											<img
												:src="`${$store.state.config.s3ResourceHost}/images/student-loans/info@2x.svg`"
												alt="info icon vector"
												class="info"
											/>
											<span class="tooltiptext"
												>How much extra you can pay per month. If you're not sure, play around with different amounts and see the big difference even $50 could make!</span
											>
										</div>
									</div>
                                </div>
                                <div class="input-group" v-else>
									<label for="add-to-payment" class="label"
										>Number of Years to Pay Off Loan</label
									>
									<div class="inner">
										<input
											placeholder=""
											class="input postend"
											type="number"
                                            min="1"
                                            max="50"
                                            step="any"
										    v-model="input.number_of_years"
											name="number-of-years"
											id="number-of-years"
                                            required
										/>
										<span class="end">Years</span>
										<div class="tooltip">
											<img
												:src="`${$store.state.config.s3ResourceHost}/images/student-loans/info@2x.svg`"
												alt="info icon vector"
												class="info"
											/>
											<span class="tooltiptext"
												>When you want to get your loan paid off fully. If you're not sure, play around with different years, maybe even fractions of a year, and see how much that all changes the bottom-line!</span
											>
										</div>
									</div>
                                </div>
                                <button class="btn btn-primary btn-medium right">
                                    Calculate
                                </button>
                            </div>
						</div>
                    </form>
                    <!-- Form Block END-->
				</div>
			</div>
        </div>
         <!-- Site footer -->
		<footer class="site-footer" v-if="!blankLayout">
			<div style="margin:0px auto!important;padding:0px 15px;">
				<div class="row">
					<div class="col-sm-10 offset-sm-1 col-lg-6 offset-lg-1 col-xl-5 offset-xl-2 content">
						<h6 class="footer_title">FAQs about loan payoff calculator</h6>
						<div class="accordion" v-for="index in FAQList" :key="index.id">
							<div>
								<input type="radio" @click="toggleAccordion(index.id)" name="example_accordion" :id="index.id" class="student-loan-radio accordion__input">
								<label :for="index.id" class="accordion__label">{{ index.question }}</label>
								<div class="accordion__content">
								    <p v-html="index.answer"></p>
								</div>
							</div>
						</div>

						<p class="disclaimer">
							At Savingforcollege.com, our goal is to help you make smart decisions about saving and paying for education.
							Some of the products featured in this article are from our partners, but this doesn't influence our evaluations. Our opinions are our own.
						</p>
					</div>
					<more-calculators></more-calculators>
				</div>
				<div class="row">
					<div class="col-sm-10 offset-sm-1 col-lg-6 offset-lg-1 col-xl-5 offset-xl-2 content">
						<h6 class="footer_title">Related Content</h6>

						<article class="card_related">
							<div class="card__left">
								<div class="card__image">
									<img :src="`https://webresources-savingforcollege.s3.us-west-2.amazonaws.com/images/articles/facebook_20190103180242_stock-49.jpg`" alt="Do Student Loans Have Prepayment Penalties?"/>
								</div>
							</div>
							<div class="card__right">
								<p class="card__info">
									<a href="https://www.savingforcollege.com/article/do-student-loans-have-prepayment-penalties" target="_blank">Do Student Loans Have Prepayment Penalties?</a>
								</p>
								<p class="card__detail">
									{{ `There are no formal penalties for prepaying federal student loans or private student loans. Lenders are banned from charging additional fees when a borrower makes extra payments on their student loans or pays off the student loan balance early. However, a few additional steps may be necessary to ensure that student loan prepayments are applied to the principal balance of the loan with the highest interest rate.` | capText}}
								</p>
							</div>
						</article>

						<article class="card_related">
							<div class="card__left">
								<div class="card__image">
									<img :src="`https://webresources-savingforcollege.s3.us-west-2.amazonaws.com/images/articles/facebook_20200206124953_your_year_fb.jpeg`" alt="Debt Snowball VS Debt Avalanche Method When Repaying Student Loans" />
								</div>
							</div>
							<div class="card__right">
								<p class="card__info">
									<a href="https://www.savingforcollege.com/article/debt-snowball-method-vs-debt-avalanche-method-when-repaying-student-loans" target="_blank">
                                    Debt Snowball VS Debt Avalanche Method When Repaying Student Loans</a>
								</p>
								<p class="card__detail">
									{{ `Roughly 70% of U.S. college graduates have student loan debt. When it comes to repaying student loan debt quicker, you have to consider what is the best way to attack it. There are several debt repayment methods – the debt snowball method, the debt avalanche method and the debt snowflake method. Let's take a closer look to see how each of these works and determine which is right for you.` | capText}}
								</p>
							</div>
						</article>

						<article class="card_related">
							<div class="card__left">
								<div class="card__image">
									<img :src="`https://webresources-savingforcollege.s3.us-west-2.amazonaws.com/images/articles/facebook_20191211111927_selling_fb.jpeg`" alt="How to Sell Your Stuff to Help Pay Down Student Loans" />
								</div>
							</div>
							<div class="card__right">
								<p class="card__info">
									<a href="https://www.savingforcollege.com/article/how-to-sell-your-stuff-to-help-pay-down-student-loans" target="_blank">
                                   How to Sell Your Stuff to Help Pay Down Student Loans</a>
								</p>
								<p class="card__detail">
									{{ `Dealing with student loan debt can be overwhelming, especially if you're struggling to make payments or just making the minimum payment every month. If you've made your budget and still don't have money to put towards your debt, it's time to find ways to make extra money. One way to get out of student loan debt faster is to sell your unwanted things for extra cash.` | capText}}
								</p>
							</div>
						</article>
					</div>
				</div>
			</div>
		</footer>
    </div>
</template>

<script>
import VueScrollTo from 'vue-scrollto'
import { LoanPrepaymentCalculator } from 'sfc_calcs_js'
import sfcShareComponent from '@/common/components/share-component'
import MoreCalculators from '@/common/components/calculators/more-calculators'

export default {
	layout: 'default_student_loan',
	components: {
		sfcShareComponent,
		MoreCalculators
	},
    data(){
		let blankLayout = false
		if(this.$route.query.layout && this.$route.query.layout == "blank"){
			blankLayout = true
		}

		return {
			blankLayout,
			input: {
				current_loan: null,
				interest_rate: null,
				monthly_payment: null,
                add_to_payment: null,
                number_of_years:null,
                pay_type:"Pay Extra"
            },
            output: {
                timeSaved: null,
                moneySaved: null,
			},
			resultDisplayed:false,
			FAQList:[
                {id: 1,
                question: "How does student loan prepayment work?",
                answer: "Making extra payments towards your student loan debt can help you pay off your loan balance quicker. You can pay more than the minimum or make multiple payments during the month. However, you need to confirm with each lender how prepayment works, so your payment is applied as an extra payment to the principal balance of the right loan and not treated as an early payment of the next installment."},
                {id: 2,
                question: "What are the benefits of making extra payments to student loans?",
                answer: "Benefits of prepaying on your student loans and paying more than the minimum required payment include: paying off your student loans faster, saving money on interest and improving your debt-to-income ratio. The faster you pay off your student loan debt, the quicker you will be able to contribute money into a savings account or retirement account."},
                {id: 3,
                question: "What are the drawbacks of making extra payments on your student loans?",
                answer: "If you are making extra student loan payments, you may not be prioritizing other important financial moves, such as having an emergency fund. If you don't tell your lender how you want your extra payment applied, it may not go towards the loan it should. If you are seeking loan forgiveness, making extra payments may reduce the amount of loan forgiveness."},
                {id: 4,
                question: "Which student loans should I pay extra towards first?",
                answer: "Make extra payments on the student loans with the highest interest rates and private student loans. It's a good idea to focus extra payments on private student loans over federal student loans, since federal student loans are eligible for forgiveness, income-based repayment and more generous deferments."},
                {id: 5,
                question: "How can I get extra money to help pay off student loans early?",
                answer: "The two main ways to get extra money to repay your student loans quicker is to increase your income and cut spending. Increase your income by asking for a raise, finding a better-paying job, getting a job that offers student loan repayment benefits, freelancing, taking on gig work or selling your unwanted belongings. Cut spending by analyzing your budget to see where you can reduce spending. You can also find creative ways. Use a cash-back credit card to pay for everyday purchases (such as groceries and gas), pay off your balance in full every month and use that cash back towards your student loans. Sallie Mae offers a credit card that gives a 25% bonus if you use your cash rewards to pay off any federal or private student loan."},
                {id: 6,
                question: "When does it not make sense to pay extra towards student loans?",
                answer: "There are times when it may not make sense to pay off student loans early. This includes if you are working towards Public Service Loan Forgiveness or if you have other debt that has a higher interest rate, such as high-interest credit cards. It may also be better to build a solid emergency fund before you pay extra towards your student loans."},
				{id: 7,
                question: "Is there anything to help me pay extra towards student loans?",
                answer: "You can use an app that helps you save and pay off loans faster. The <a href='https://www.savingforcollege.com/article/changed-app-review-pay-off-student-loans-with-spare-change'>ChangEd</a> app rounds up your purchases and applies that towards your student loan debt. For example, if you purchase lunch for $10.50, it will round up your total to $11 and that 50 cents is applied to your student loan debt."}
			]
		}
	},
	computed: {
		calculatedMinMonthlyPayment(){
			// The minimum monthly payment can not be a value that will cause the principalPaid value in the calculator's formula to become <= 0, this will cause an infinite loop
			if(this.input.current_loan == null || this.input.interest_rate == null){
				return 50;
			}else{
				// From sfc_calcs_js/src/loan_prepayment_calculator.js
				const interestPaid = parseFloat(this.input.current_loan) * (parseFloat(this.input.interest_rate)/(100*12));
				// The monthly payment CAN NOT be less or equal than the interestPaid value
				return Math.round(interestPaid + 1);
			}
		}
	},
	mounted() {
		document.body.className = 'gray';
		setTimeout(() => {
			document.getElementById("sfc-main-content").classList.add("student-loan-calculator-content");
		}, 1000)
	},
    methods: {
		recalculate($event){

            let calc = new LoanPrepaymentCalculator({
                loanBalance: this.input.current_loan,
                annualInterestRate: this.input.interest_rate,
                monthlyPayment: this.input.monthly_payment,
                extraPayment: this.input.add_to_payment==null?0:this.input.add_to_payment,
                payType:this.input.pay_type,
                numberOfYears:this.input.number_of_years==null?1:this.input.number_of_years
            })

            this.results = calc.calculate().output;

            this.output.moneySaved = this.results.moneySaved;
            this.output.timeSaved = this.results.timeSaved;

            this.resultDisplayed = true;
            setTimeout(() => {
				VueScrollTo.scrollTo('#res');
			}, 200)
			$event.preventDefault();
		},
		formatNumber(number)
		{
			let parsedNumber = Math.abs(number)
			parsedNumber = Number(parsedNumber).toLocaleString();

			let string = parsedNumber.split('.');
			if(string[1]!=undefined) {
				string = string[1];

				if(string.length==1){
					parsedNumber+="0";
				}
			} else {
				parsedNumber+=".00";
			}

			if (number < 0) {
				return `-$${parsedNumber}`
			}
			return `$${parsedNumber}`
		},
		toggleAccordion(section){
			if (this.lastSectionOpened == section) {
				let input = document.getElementById(section)
				if (input.checked) {
					input.checked = false
				}
				this.lastSectionOpened = ""
			} else {
				this.lastSectionOpened = section
			}
		},
        create_schema(FAQList) {
            let jsonld = '{ "@context": "https://schema.org", "@type": "FAQPage", "mainEntity": [';
            for (var i = 0; i < FAQList.length; i++) {
                if((i + 1) == FAQList.length){
                    jsonld += '{ "@type": "Question", "name": "' + this.FAQList[i].question +'" , "acceptedAnswer": { "@type": "Answer", "text": "' + this.FAQList[i].answer + '" }}';
                }else{
                    jsonld += '{ "@type": "Question", "name": "' + this.FAQList[i].question +'" , "acceptedAnswer": { "@type": "Answer", "text": "' + this.FAQList[i].answer + '"}},';
                }
            }
            jsonld += ']}';
            return jsonld;
        }
	},
	head(){
		return {
			title: "Student Loan Payoff Calculator",
			meta: [
				{hid: "ogmetatitle", property: 'og:title', content: "Student Loan Payoff Calculator"},
				{hid: "metadescription", name: 'description', content: "Use our student loan payoff calculator to see how extra payments can help you become debt-free faster and save money."},
				{hid: "ogmetadescription", property: 'og:description', content: "Use our student loan payoff calculator to see how extra payments can help you become debt-free faster and save money."},
				{
					hid: 'og-url',
					name: 'og:url',
					property: 'og:url',
					content: `${this.$store.state.config.sfcHost}/calculators/student-loan-payoff-calculator`
				},
				{
					hid: 'og-image',
					name: 'og:image',
					property: 'og:image',
					content: 'https://web-resources.savingforcollege.com/images/cost-of-deferment-calculator-thumbnail.jpg'
				},
			],
			__dangerouslyDisableSanitizers: ['title', 'meta']
		}
	},
	filters: {
		capText(value){
			if(value.length < 165) {
				return value
			}
			let paragraph = ""
			let i = 0
			const words = value.split(" ")
			while (paragraph.length < 165 && (paragraph.length + words[i].length < 165)) {
				paragraph += ` ${words[i]}`
				i++
			}
			return paragraph.trim() + "..."
		}
	}
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
    /* I have included page specific styles to the card here, widths and classes specifically for this calculator the rest of the main styles can be found in the "styles.css" */
    .sfc-card {
        width: 70%;
        margin-top: 40px;
        margin-left: 15%;
    }
    .sfc-card .form {
        width: 100%;
        margin: 0px 0%;
    }
    .form .btn {
        margin-top: 56px;
    }
    .checkbox-group{
        padding: 0 20px 10px 20px;
    }
    .checkbox-group .tooltip{
        margin-top: 20px;
    }
	.checkbox-group label {
		margin-top: 10px !important;
		margin-bottom: 0 !important;
	}
    .small-card{
        border-radius: 6px;
        background: #f1f5f8;
        box-sizing: border-box;
        padding: 20px 0px 10px 0px;
        margin: 20px;
    }
    .results {
        width: 94%;
        padding: 20px 0px;
        margin-left: 0px;
        box-sizing: border-box;
    }
    .graybg {
        border-radius: 0px 8px 8px 0px;
    }
	.disclaimer {
		font-style: italic;
		color: #000;
		margin-top: 30px;
	}
    @media only screen and (max-width: 1220px) {
        .sfc-card {
            width: 90%;
            margin-top: 40px;
            margin-left: 4%;
        }
        .sfc-card.completed {
            width: 100%;
            margin-left: 0%;
        }
    }

    @media only screen and (max-width: 768px) {
        .sfc-card {
            width: 100%;
            margin-top: 40px;
            margin-left: 0%;
            display: inline-block;
        }
        .form .flex {
            display: inline-block !important;
        }
        .sfc-card.completed .form {
            width: 90%;
        }
        .sfc-card .form {
            width: 100%;
        }
        .flex.checkbox-group{
            display: flex !important;
            width: 100%;
        }
        .bluebg {
            background: #125393;
            color: #fff;
            border-radius: 0px;
        }
        .sfc-col-12.flex{
            display: inline-block !important;
        }
        .small-card{
            padding: 20px 0px 10px 0px;
            margin: 10px 0px;
            width: 100%;
        }
    }
</style>
